// src/pages/cv/api/cvCertificateFiles.js
// Shared helpers for employee-CV certificate attachments.
// The file itself is served by the API (owner or CV reviewer only), so it must
// be fetched as a blob with the auth header instead of being linked directly.
import api from "../../../services/api";

export const hasCertificateFile = (cert) =>
  Boolean(cert?.file_url || String(cert?.file_path || "").trim());

export const certificateFileName = (cert) => {
  if (cert?.file_name) return String(cert.file_name);
  const raw = String(cert?.file_path || "").trim();
  if (!raw) return "";
  const clean = raw.split("?")[0].split("#")[0];
  return clean.split("/").pop() || clean;
};

export const isExternalCertificateFile = (cert) =>
  Boolean(cert?.file_is_external) || /^https?:\/\//i.test(String(cert?.file_path || "").trim());

/** Upload an attachment and get back the path to store in certificates[].file_path */
export async function uploadCertificateFile(file, userId = null) {
  const fd = new FormData();
  fd.append("file", file);
  if (userId) fd.append("user_id", String(userId));

  const res = await api.post("/cv/certificates/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return res?.data?.data || res?.data || null;
}

const fetchBlob = async (cert, download) => {
  const id = cert?.id;
  if (!id) {
    const err = new Error("Save the CV first, then the attachment can be opened.");
    err.isUnsaved = true;
    throw err;
  }
  const res = await api.get(`/cv/certificates/${id}/file`, {
    responseType: "blob",
    params: download ? { download: 1 } : undefined,
  });
  return res?.data;
};

/** Open the attachment in a new tab. */
export async function openCertificateFile(cert) {
  const externalUrl = isExternalCertificateFile(cert)
    ? cert?.file_url || String(cert?.file_path || "").trim()
    : null;

  if (externalUrl) {
    window.open(externalUrl, "_blank", "noopener,noreferrer");
    return;
  }

  const blob = await fetchBlob(cert, false);
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  if (!win) throw new Error("Your browser blocked the popup. Allow popups or use Download.");
}

/** Download the attachment to disk. */
export async function downloadCertificateFile(cert) {
  const externalUrl = isExternalCertificateFile(cert)
    ? cert?.file_url || String(cert?.file_path || "").trim()
    : null;

  if (externalUrl) {
    window.open(externalUrl, "_blank", "noopener,noreferrer");
    return;
  }

  const blob = await fetchBlob(cert, true);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = certificateFileName(cert) || `certificate_${cert?.id || ""}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
